import "./style.css";
import { track } from "./analytics";
import {
  clearCalendarSession,
  connectGoogle,
  dayRange,
  fetchGoogleBusy,
  googleClientId,
  parseIcs,
  setGoogleClientId,
  saveIcs,
  savedGoogleToken,
  savedIcs,
  slotConflicts,
} from "./calendar";
import { mapHtml } from "./map";
import { COLORS, detectLocalPlace, findPlace, placeFromIana, placeId, searchPlaces, type Place } from "./places";
import { fetchIdeas, feedbackHtml, isFeedbackPath, postIdea, voteIdea, type Idea } from "./feedback";
import { isPrivacyPath, privacyHtml } from "./privacy";
import { storyHtml } from "./story";
import {
  applyRoom,
  connectRoom,
  createRoom,
  fetchRoom,
  myMemberId,
  patchRoom,
  personToRoom,
  roomIdFromPath,
  roomUrl,
} from "./room";
import {
  DURATIONS,
  decodeState,
  encodeState,
  googleCalendarUrl,
  parseDuration,
  meetUrl,
  meetingStartUtc,
  outlookUrl,
  type Person,
  type PlannerState,
} from "./share";
import {
  formatClock,
  formatHour,
  formatPrettyDate,
  formatUtcClock,
  isSleepHour,
  isWorkHour,
  longName,
  parseDate,
  partsInZone,
  shiftDate,
  shortName,
  todayInZone,
  utcOffsetLabel,
  zonedLocalToUtc,
} from "./time";

const NAME_KEY = "meetingly_name";
const app = document.querySelector<HTMLDivElement>("#app")!;

function savedDisplayName(): string {
  return (sessionStorage.getItem(NAME_KEY) ?? "").trim();
}

const ICONS = {
  plus: '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>',
  x: '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>',
  left: '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m15 18-6-6 6-6"/></svg>',
  right: '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>',
  info: '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>',
  link: '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
  cal: '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>',
  video: '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m16 13 5.2 3.1a.5.5 0 0 0 .8-.4V8.3a.5.5 0 0 0-.8-.4L16 11"/><rect x="2" y="7" width="14" height="10" rx="2"/></svg>',
  check: '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 7V3M16 7V3M3 11h18M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z"/><path d="m9 16 2 2 4-4"/></svg>',
  upload: '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 16V4M7 9l5-5 5 5"/><path d="M4 20h16"/></svg>',
  reset: '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>',
};

let now = new Date();
let showHow = false;
let showSearch = false;
let query = "";
let highlight = 0;
let toast: string | null = null;
let toastTimer = 0;
let showCalMenu = false;
let showGoogleSetup = false;
let calLoading = false;
let ideas: Idea[] = [];
let ideasLoaded = false;
let ideasError = "";

function freshState(): PlannerState {
  const you = detectLocalPlace();
  const person: Person = { ...you, color: COLORS[0], isYou: true, memberId: myMemberId(), busy: [] };
  return {
    people: [person],
    date: todayInZone(you.iana),
    duration: 60,
    selectedHour: null,
    activeId: placeId(you),
    busy: [],
    finalized: false,
  };
}

let state: PlannerState = decodeState(location.hash) ?? freshState();
let roomId = roomIdFromPath();
let roomExpiresAt = 0;
let stopRoom: (() => void) | null = null;

function writeHash() {
  if (roomId || isPrivacyPath()) return;
  const next = encodeState(state);
  const hash = `#${next}`;
  if (location.hash !== hash) history.replaceState(null, "", hash);
}

function columnInstant(hour: number): Date {
  const host = state.people[0];
  const { year, month, day } = parseDate(state.date);
  return zonedLocalToUtc(year, month, day, hour, 0, host?.iana ?? "UTC");
}

function personAtHour(person: Person, hour: number) {
  return partsInZone(columnInstant(hour), person.iana);
}

function allBusy() {
  const nested = state.people.flatMap((p) => p.busy ?? []);
  return nested.length ? nested : state.busy;
}

function hourIsBusy(hour: number): boolean {
  return slotConflicts(columnInstant(hour), state.duration, allBusy());
}

function personHourBusy(person: Person, hour: number): boolean {
  return slotConflicts(columnInstant(hour), state.duration, person.busy ?? []);
}

function busyColumnCount(): number {
  return Array.from({ length: 24 }, (_, hour) => hour).filter(hourIsBusy).length;
}

function mine(): Person | undefined {
  return state.people.find((p) => p.isYou) ?? state.people[0];
}

async function setBusy(busy: typeof state.busy) {
  const me = myMemberId();
  const selected = state.selectedHour;
  let attached = false;
  const people = state.people.map((p) => {
    if (p.isYou || p.memberId === me) {
      attached = true;
      return { ...p, memberId: p.memberId || me, busy, ready: true };
    }
    return p;
  });
  if (!attached && people[0]) {
    people[0] = { ...people[0], busy, ready: true };
  }
  state = {
    ...state,
    people,
    busy,
    selectedHour: selected !== null && slotConflicts(columnInstant(selected), state.duration, busy) ? null : selected,
  };
  await publishMine();
}

async function publishMine() {
  if (!roomId) return;
  const person = mine();
  if (!person) return;
  try {
    const room = await patchRoom(roomId, {
      upsert: personToRoom(person, person.memberId || myMemberId()),
      date: state.date,
      duration: state.duration,
      selectedHour: state.selectedHour,
    });
    roomExpiresAt = room.expiresAt;
    state = applyRoom(state, room, myMemberId());
  } catch (err) {
    if (err instanceof Error && err.message === "expired") onRoomExpired();
  }
}

function onRoomExpired() {
  stopRoom?.();
  stopRoom = null;
  roomId = null;
  roomExpiresAt = 0;
  flash("This private room expired and was deleted");
  history.replaceState(null, "", "/");
}

async function startRoom(id: string) {
  try {
    const room = await fetchRoom(id);
    roomId = id;
    roomExpiresAt = room.expiresAt;
    state = applyRoom(state, room, myMemberId());
    if (!state.people.some((p) => p.memberId === myMemberId())) {
      const local: Person = {
        ...detectLocalPlace(),
        color: COLORS[state.people.length % COLORS.length],
        isYou: true,
        memberId: myMemberId(),
        busy: [],
      };
      state = {
        ...state,
        people: [...state.people.map((p) => ({ ...p, isYou: false })), local],
      };
      await patchRoom(id, { upsert: personToRoom(local, local.memberId!) });
      track("participant_joined");
    }
    stopRoom?.();
    stopRoom = connectRoom(
      id,
      (snap) => {
        roomExpiresAt = snap.expiresAt;
        state = applyRoom(state, snap, myMemberId());
        render();
      },
      onRoomExpired,
    );
    if (location.pathname !== `/r/${id}`) history.replaceState(null, "", `/r/${id}`);
    render();
  } catch (err) {
    roomId = null;
    flash(err instanceof Error && err.message === "expired" ? "This private room expired" : "Room not found");
    history.replaceState(null, "", "/");
    render();
  }
}

async function sharePrivateRoom() {
  try {
    const created = await createRoom({
      ...state,
      people: state.people.map((p, i) => ({
        ...p,
        memberId: p.memberId || (p.isYou || i === 0 ? myMemberId() : crypto.randomUUID()),
      })),
    });
    await startRoom(created.id);
    const url = roomUrl(created.id);
    await navigator.clipboard.writeText(url);
    track("room_created");
    track("room_link_copied");
    flash("Scheduling room link copied — it expires in 24 hours");
  } catch {
    flash("Could not create a private room");
    render();
  }
}

function roomExpiryLabel(): string {
  if (!roomExpiresAt) return "";
  const ms = roomExpiresAt - Date.now();
  if (ms <= 0) return "expired";
  const hours = Math.max(1, Math.ceil(ms / 3_600_000));
  return hours === 1 ? "expires in ~1h" : `expires in ${hours}h`;
}

async function refreshBusyFromSources() {
  const host = state.people[0];
  if (!host) return;
  const range = dayRange(state.date, host.iana);
  const token = savedGoogleToken();
  if (token) {
    try {
      calLoading = true;
      render();
      const busy = await fetchGoogleBusy(token, range.start, range.end);
      calLoading = false;
      await setBusy(busy);
      render();
      return;
    } catch {
      calLoading = false;
    }
  }
  const ics = savedIcs();
  if (ics) {
    await setBusy(parseIcs(ics, host.iana, range.start, range.end));
    render();
  }
}

async function syncGoogle() {
  if (!googleClientId()) {
    showCalMenu = true;
    showGoogleSetup = true;
    render();
    return;
  }
  try {
    calLoading = true;
    showCalMenu = false;
    showGoogleSetup = false;
    track("calendar_sync_started");
    render();
    const token = savedGoogleToken() ?? (await connectGoogle());
    const host = mine() ?? state.people[0];
    const range = dayRange(state.date, host.iana);
    const busy = await fetchGoogleBusy(token, range.start, range.end);
    calLoading = false;
    await setBusy(busy);
    track("calendar_sync_completed");
    track("availability_submitted");
    flash(busy.length ? "Signed in — busy hours pulled from Google" : "Signed in — Google Calendar is free that day");
    render();
  } catch (err) {
    calLoading = false;
    track("calendar_sync_failed");
    const message = err instanceof Error ? err.message : "Google sign-in failed";
    flash(message === "missing-client-id" ? "Add a Google client ID to sign in" : message);
    showCalMenu = true;
    render();
  }
}

async function syncIcs(file: File) {
  const text = await file.text();
  saveIcs(text);
  const host = state.people[0];
  if (!host) return;
  const range = dayRange(state.date, host.iana);
  const busy = parseIcs(text, host.iana, range.start, range.end);
  await setBusy(busy);
  showCalMenu = false;
  track("calendar_sync_completed");
  track("availability_submitted");
  flash(busy.length ? "Busy hours pulled from .ics" : "No events found on this day");
  render();
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}

function results(): Place[] {
  return searchPlaces(query).filter(
    (place) => !state.people.some((p) => placeId(p) === placeId(place)),
  );
}

function addPlace(place: Place) {
  if (state.people.length >= COLORS.length) {
    flash("Six time zones is the max");
    return;
  }
  if (state.people.some((p) => placeId(p) === placeId(place))) return;
  const person: Person = {
    ...place,
    color: COLORS[state.people.length % COLORS.length],
    isYou: false,
    memberId: crypto.randomUUID(),
    busy: [],
  };
  state = { ...state, people: [...state.people, person], activeId: placeId(person) };
  showSearch = false;
  query = "";
  track("timezone_added");
  if (roomId) {
    void patchRoom(roomId, { upsert: personToRoom(person, person.memberId!) }).catch(() => onRoomExpired());
  }
  render();
}

function resetPeople() {
  state = {
    ...state,
    people: [],
    selectedHour: null,
    activeId: null,
    busy: [],
  };
  showSearch = false;
  query = "";
  if (roomId) {
    void patchRoom(roomId, { clearPeople: true, selectedHour: null }).catch(() => onRoomExpired());
  }
  flash("All timezones cleared");
  render();
}

function loadDemo() {
  const cities = [
    placeFromIana("America/Toronto"),
    placeFromIana("Europe/London"),
    placeFromIana("Asia/Singapore"),
  ];
  const people: Person[] = cities.map((place, i) => ({
    ...place,
    color: COLORS[i % COLORS.length],
    isYou: i === 0,
    memberId: i === 0 ? myMemberId() : crypto.randomUUID(),
    busy: [],
    displayName: ["Alex", "Sam", "Mei"][i],
    ready: i !== 1,
  }));
  state = {
    ...state,
    people,
    date: state.date,
    selectedHour: 9,
    activeId: placeId(people[0]),
    busy: [],
    finalized: false,
  };
  render();
}

function submitAvailability() {
  const input = document.querySelector<HTMLInputElement>("#display-name");
  const name = (input?.value ?? savedDisplayName()).trim().slice(0, 40);
  if (name) sessionStorage.setItem(NAME_KEY, name);
  const me = myMemberId();
  const people = state.people.map((p) =>
    p.isYou || p.memberId === me ? { ...p, memberId: p.memberId || me, displayName: name, ready: true } : p,
  );
  state = { ...state, people };
  track("availability_submitted");
  if (roomId) {
    const person = people.find((p) => p.memberId === me || p.isYou);
    if (person) void patchRoom(roomId, { upsert: personToRoom(person, person.memberId || me) }).catch(() => onRoomExpired());
  }
  flash(name ? `${name} is in` : "You’re marked as in");
  render();
}

function flash(message: string) {
  toast = message;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toast = null;
    render();
  }, 1600);
}

function slotSummary(): string {
  if (state.selectedHour === null) return "";
  return state.people
    .map((p) => `${formatClock(columnInstant(state.selectedHour!), p.iana)} ${p.city}`)
    .join(" / ");
}

function detailsText(): string {
  const lines = state.people.map((p) => {
    const start = formatClock(columnInstant(state.selectedHour ?? 0), p.iana);
    return `${p.city} (${shortName(columnInstant(state.selectedHour ?? 0), p.iana)}): ${start}`;
  });
  return `Meeting across time zones\n${lines.join("\n")}`;
}

function render() {
  if (isPrivacyPath()) {
    app.innerHTML = privacyHtml(escapeHtml(formatUtcClock(now)));
    return;
  }
  if (isFeedbackPath()) {
    app.innerHTML = feedbackHtml(escapeHtml(formatUtcClock(now)), ideas, !ideasLoaded, ideasError);
    if (!ideasLoaded) {
      ideasLoaded = true;
      void fetchIdeas()
        .then((list) => {
          ideas = list;
          render();
        })
        .catch(() => {
          ideasError = "Could not load ideas.";
          render();
        });
    }
    return;
  }
  writeHash();
  const host = state.people[0];
  const matches = results();
  const start = meetingStartUtc(state);
  const calUrl = start
    ? googleCalendarUrl("Meetingly meeting", start, state.duration, detailsText())
    : "#";
  const outUrl = start ? outlookUrl("Meetingly meeting", start, state.duration, detailsText()) : "#";
  const readyCount = state.people.filter((p) => p.ready).length;

  app.innerHTML = `
    <div class="app-shell">
      <a class="skip-link" href="#planner">Skip to planner</a>
      <header class="header glass">
        <div class="brand-block">
          <div class="brand">
            <img class="brand-logo" src="/logo.jpg" alt="" />
            Meetingly
          </div>
          <p class="tagline">Find a meeting time across time zones—without accounts, spreadsheets, or endless messages.</p>
        </div>
        <div class="header-right">
          <div class="live-utc mono"><span class="pulse"></span>${escapeHtml(formatUtcClock(now))}</div>
          ${
            roomId
              ? `<span class="room-pill mono">${escapeHtml(roomExpiryLabel())}</span>`
              : `<button class="btn-ghost" data-act="share-room">${ICONS.link} <span class="btn-label">Create a scheduling room</span></button>`
          }
          ${roomId ? "" : `<button class="btn-ghost" data-act="demo">Try example</button>`}
          <button class="btn-ghost" data-act="how">${ICONS.info} <span class="btn-label">How it works</span></button>
          <a class="btn-ghost" href="/featurerequests">Feedback</a>
          <a class="btn-ghost" href="/privacy">Privacy</a>
        </div>
      </header>

      <div class="map-stage" data-map>
        ${mapHtml(state.people, state.activeId, state.selectedHour !== null ? columnInstant(state.selectedHour) : now)}
      </div>

      <div class="workspace" id="planner">
        <div class="people-block">
          <div class="people-row">
            ${
              state.people.length
                ? state.people
                    .map((person) => {
                      const id = placeId(person);
                      return `
                <button class="chip glass ${id === state.activeId ? "is-active" : ""}" data-act="focus" data-id="${escapeHtml(id)}" style="--chip:${person.color}">
                  <span class="chip-dot" style="background:${person.color}"></span>
                  <span class="chip-copy">
                    <span class="chip-title">
                      <strong>${escapeHtml(person.displayName || person.city)}</strong>
                      ${person.displayName ? `<span>${escapeHtml(person.city)}</span>` : person.country ? `<span>${escapeHtml(person.country)}</span>` : ""}
                      ${person.isYou ? `<span class="you-tag">You</span>` : ""}
                      <span class="status-tag ${person.ready ? "is-ready" : "is-wait"}">${person.ready ? "Ready" : "Waiting"}</span>
                    </span>
                    <span class="chip-meta">
                      <span class="mono">${escapeHtml(shortName(now, person.iana))}</span>
                      <span>${escapeHtml(longName(now, person.iana))}</span>
                    </span>
                  </span>
                  <span class="chip-time">
                    <span class="clock mono">${escapeHtml(formatClock(now, person.iana))}</span>
                    <span class="offset mono">${escapeHtml(utcOffsetLabel(now, person.iana))}</span>
                  </span>
                  <span class="chip-remove" data-act="remove" data-id="${escapeHtml(id)}" aria-label="Remove">${ICONS.x}</span>
                </button>
              `;
                    })
                    .join("")
                : `<div class="people-empty">No timezones yet — add cities for the people on the call.</div>`
            }
          </div>
          <div class="people-actions">
            <button class="add-btn glass" data-act="search">${ICONS.plus} Add timezone</button>
            <button class="reset-btn glass" data-act="reset" ${state.people.length ? "" : "disabled"}>${ICONS.reset} Reset</button>
            ${
              roomId
                ? `<form class="name-form" data-act="noop">
                    <label class="duration-label mono" for="display-name">Your name</label>
                    <input id="display-name" type="text" maxlength="40" placeholder="Alex" value="${escapeHtml(mine()?.displayName || savedDisplayName())}" autocomplete="nickname" />
                    <button class="add-btn glass" data-act="ready" type="button">I'm in</button>
                  </form>`
                : ""
            }
          </div>
          <p class="trust-note">No account required · Meeting titles are never stored · Private rooms expire automatically. <a href="/privacy">Privacy</a></p>
          ${
            roomId && state.people.length
              ? `<p class="roster-note" aria-live="polite">${readyCount} of ${state.people.length} ready${
                  state.people.some((p) => !p.ready)
                    ? ` · waiting on ${state.people
                        .filter((p) => !p.ready)
                        .map((p) => p.displayName || p.city)
                        .join(", ")}`
                    : ""
                }</p>`
              : ""
          }
        </div>

        <div class="day-strip">
          <div class="date-nav">
            <button data-act="prev" aria-label="Previous day">${ICONS.left}</button>
            <span class="date-label">${escapeHtml(formatPrettyDate(state.date))}</span>
            <button data-act="next" aria-label="Next day">${ICONS.right}</button>
          </div>
          <div class="cal-wrap">
            <button class="cal-btn glass ${allBusy().length ? "is-on" : ""}" data-act="cal-menu">
              ${ICONS.cal}
              ${calLoading ? "Reading calendar…" : allBusy().length ? `${busyColumnCount()} hours blocked` : "Sync calendar"}
            </button>
            ${
              showCalMenu
                ? `<div class="cal-pop glass" data-act="noop">
                    <p>${
                      roomId
                        ? "Google will ask only for busy/free times (calendar.freebusy). Meeting titles and guests are not requested."
                        : "Google will ask only for busy/free times. Meeting titles and guests are not requested."
                    } <a href="/privacy">Privacy policy</a></p>
                    <button data-act="cal-google">${ICONS.cal} Sign in with Google</button>
                    ${
                      !googleClientId() || showGoogleSetup
                        ? `<div class="google-setup">
                            <p>Google needs a one-time OAuth client (free). Takes about two minutes:</p>
                            <ol>
                              <li>Open <a href="https://console.cloud.google.com/apis/library/calendar-json.googleapis.com" target="_blank" rel="noreferrer">Google Calendar API</a> and click Enable.</li>
                              <li>Open <a href="https://console.cloud.google.com/auth/clients/create" target="_blank" rel="noreferrer">Create OAuth client</a> → Web application.</li>
                              <li>Under Authorized JavaScript origins add <code>http://localhost:5173</code>.</li>
                              <li>Create, copy the Client ID, paste it here.</li>
                            </ol>
                            <input id="google-client-id" type="text" placeholder="….apps.googleusercontent.com" value="${escapeHtml(googleClientId())}" autocomplete="off" />
                            <button data-act="cal-google-save">Save and sign in</button>
                          </div>`
                        : ""
                    }
                    <button data-act="cal-ics">${ICONS.upload} Upload .ics instead</button>
                    ${allBusy().length ? `<button data-act="cal-clear">${ICONS.x} Clear my busy hours</button>` : ""}
                    <input id="ics-file" type="file" accept=".ics,text/calendar" hidden />
                  </div>`
                : ""
            }
          </div>
          <div class="duration-wrap">
            <span class="duration-label mono">Meeting duration</span>
            <div class="durations glass">
              ${DURATIONS.map(
                (mins) =>
                  `<button data-act="dur" data-mins="${mins}" class="${state.duration === mins ? "is-on" : ""}">${mins}m</button>`,
              ).join("")}
            </div>
          </div>
        </div>

        <div class="grid-wrap">
          ${
            !host
              ? `<div class="grid-empty">Add a timezone to see the day grid.</div>`
              : `<div class="grid" style="--rows:${state.people.length}">
            <div class="grid-corner">${escapeHtml(host.city)}</div>
            ${Array.from({ length: 24 }, (_, hour) => `<div class="grid-hour mono ${hourIsBusy(hour) ? "is-busy" : ""}">${formatHour(hour)}</div>`).join("")}
            ${state.people
              .map((person) => {
                const cells = Array.from({ length: 24 }, (_, hour) => {
                  const local = personAtHour(person, hour);
                  const sleep = isSleepHour(local.hour);
                  const work = isWorkHour(local.hour);
                  const overlap =
                    state.people.length > 1 &&
                    state.people.every((p) => isWorkHour(personAtHour(p, hour).hour));
                  const selected = state.selectedHour === hour;
                  const blocked = hourIsBusy(hour);
                  const theirs = personHourBusy(person, hour);
                  const freeCount = state.people.filter((p) => !personHourBusy(p, hour)).length;
                  const allFree = state.people.length > 1 && !blocked && freeCount === state.people.length;
                  const showCounts = (readyCount > 0 || blocked) && state.people.length > 1;
                  const slotLabel = `${formatHour(hour)} ${person.city}, ${freeCount} of ${state.people.length} available${theirs ? ", busy" : ""}${allFree ? ", everyone free" : ""}`;
                  return `<button class="grid-cell ${sleep ? "sleep" : ""} ${work ? "work" : ""} ${overlap ? "overlap" : ""} ${selected ? "is-selected" : ""} ${theirs || blocked ? "is-busy" : ""} ${showCounts && allFree ? "all-free" : ""}" data-act="${state.finalized ? "locked" : blocked ? "busy" : "slot"}" data-hour="${hour}" aria-label="${escapeHtml(slotLabel)}" title="${escapeHtml(slotLabel)}">
                    ${selected ? `<span class="mark-x">×</span>` : ""}
                    ${theirs || blocked ? `<span class="busy-tag">busy</span>` : showCounts && allFree ? `<span class="free-tag">all free</span>` : showCounts ? `<span class="count-tag">${freeCount}/${state.people.length}</span>` : ""}
                    ${escapeHtml(formatHour(local.hour))}
                  </button>`;
                }).join("");
                return `<div class="grid-name"><span class="dot" style="background:${person.color}"></span>${escapeHtml(person.city)}</div>${cells}`;
              })
              .join("")}
            <div class="grid-baseline" aria-hidden="true"></div>
          </div>`
          }
        </div>
      </div>

      <div class="share-dock glass ${state.selectedHour !== null ? "is-on" : ""}">
        <div class="share-copy">
          <div class="share-icon">${ICONS.check}</div>
          <div class="share-copy-text">
            <h2>${state.finalized ? "Meeting time locked" : "This time works for everyone"}</h2>
            <p>${escapeHtml(formatPrettyDate(state.date))} · ${escapeHtml(slotSummary())}</p>
          </div>
        </div>
        <div class="share-actions">
          <a class="btn-ghost" href="${escapeHtml(calUrl)}" target="_blank" rel="noreferrer" data-act="gcal">${ICONS.cal} Google Calendar</a>
          <a class="btn-ghost" href="${escapeHtml(meetUrl())}" target="_blank" rel="noreferrer">${ICONS.video} Meet</a>
          <a class="btn-ghost" href="${escapeHtml(outUrl)}" target="_blank" rel="noreferrer" data-act="outlook">${ICONS.cal} Outlook</a>
          ${roomId && !state.finalized ? `<button class="btn-ghost" data-act="finalize">Lock this time</button>` : ""}
          <button class="btn-lime" data-act="copy">${ICONS.link} ${roomId ? "Copy room link" : "Copy link"}</button>
        </div>
      </div>

      ${toast ? `<div class="toast">${escapeHtml(toast)}</div>` : ""}
      ${
        showHow
          ? `<div class="modal-back" data-act="close-how">
              <div class="modal glass" data-act="noop">
                <h2>Five steps. No account.</h2>
                <ol>
                  <li><strong>Add participants or time zones</strong> — city, country, or acronym.</li>
                  <li><strong>Create a scheduling room</strong> and send the secret link.</li>
                  <li><strong>Everyone syncs a calendar or taps I’m in</strong> so busy hours grey out. Only busy/free is stored.</li>
                  <li><strong>Select a time that works for everyone.</strong></li>
                  <li><strong>Add it to Google Calendar or Outlook.</strong></li>
                </ol>
                <p class="how-privacy">No account required · Meeting titles are never stored · Rooms expire automatically. <a href="/privacy">Privacy policy</a>.</p>
                <button class="btn-lime modal-close" data-act="close-how">Got it</button>
              </div>
            </div>`
          : ""
      }
      ${
        showSearch
          ? `<div class="modal-back" data-act="close-search">
              <div class="modal glass search-modal" data-act="noop">
                <h2>Add a timezone</h2>
                <p class="search-lead">Type a city, country, or acronym — vegas, CET, EST…</p>
                <input id="tz-search" type="text" placeholder="Paris, Tokyo, EST…" value="${escapeHtml(query)}" autocomplete="off" />
                ${
                  query.trim()
                    ? matches.length
                      ? `<ul class="search-list">${matches
                          .map((place, i) => {
                            const acronym = shortName(now, place.iana);
                            const where = [place.province, place.country].filter(Boolean).join(", ");
                            return `<li><button class="${i === highlight ? "is-hi" : ""}" data-act="add" data-pid="${escapeHtml(placeId(place))}">
                              <span><strong>${escapeHtml(place.city)}</strong> · ${escapeHtml(where)}</span>
                              <span class="acronym mono">${escapeHtml(acronym)}</span>
                            </button></li>`;
                          })
                          .join("")}</ul>`
                      : `<div class="search-empty">Can't find that one — try a city like "Paris" or an acronym like "CET".</div>`
                    : `<div class="search-hint">EST is Toronto / New York. CET is Berlin, Paris, Amsterdam — not London (that's GMT/BST).</div>`
                }
                <button class="btn-ghost modal-close" data-act="close-search">Cancel</button>
              </div>
            </div>`
          : ""
      }
      ${roomId ? "" : storyHtml()}
    </div>
  `;

  const search = document.querySelector<HTMLInputElement>("#tz-search");
  if (search) {
    search.focus();
    search.setSelectionRange(query.length, query.length);
  }
}

app.addEventListener("click", (event) => {
  const target = (event.target as Element | null)?.closest("[data-act], .pin") as
    | HTMLElement
    | SVGElement
    | null;
  if (!target) {
    if (showSearch || showCalMenu) {
      showSearch = false;
      showCalMenu = false;
      render();
    }
    return;
  }
  const act = target.dataset.act;
  if (target.classList.contains("pin")) {
    const id = target.dataset.id;
    if (id) {
      state = { ...state, activeId: id };
      render();
    }
    return;
  }
  if (act === "how") {
    showHow = true;
    render();
  } else if (act === "close-how") {
    showHow = false;
    render();
  } else if (act === "close-search") {
    showSearch = false;
    query = "";
    render();
  } else if (act === "noop") {
    event.stopPropagation();
  } else if (act === "search") {
    showSearch = !showSearch;
    render();
  } else if (act === "reset") {
    resetPeople();
  } else if (act === "focus" && target.dataset.id) {
    if ((event.target as HTMLElement).closest("[data-act=remove]")) return;
    state = { ...state, activeId: target.dataset.id };
    render();
  } else if (act === "share-room") {
    void sharePrivateRoom();
  } else if (act === "demo") {
    loadDemo();
  } else if (act === "ready") {
    submitAvailability();
  } else if (act === "finalize") {
    if (!roomId || state.selectedHour === null) return;
    state = { ...state, finalized: true };
    track("meeting_finalized");
    void patchRoom(roomId, { finalized: true, selectedHour: state.selectedHour }).catch(() => onRoomExpired());
    flash("Meeting time locked");
    render();
  } else if (act === "start") {
    document.getElementById("planner")?.scrollIntoView({ behavior: "smooth" });
  } else if (act === "locked") {
    flash("This time is already locked");
  } else if (act === "gcal") {
    track("google_calendar_clicked");
  } else if (act === "outlook") {
    track("outlook_clicked");
  } else if (act === "idea-submit") {
    const title = document.querySelector<HTMLInputElement>("#idea-title")?.value ?? "";
    const body = document.querySelector<HTMLTextAreaElement>("#idea-body")?.value ?? "";
    void postIdea(title, body)
      .then((idea) => {
        ideas = [idea, ...ideas.filter((row) => row.id !== idea.id)];
        ideasError = "";
        track("feedback_submitted");
        render();
      })
      .catch((err: unknown) => {
        ideasError = err instanceof Error ? err.message : "Could not post";
        render();
      });
  } else if (act === "idea-vote") {
    const id = (target as HTMLElement).dataset.id;
    if (!id) return;
    void voteIdea(id)
      .then(() => {
        ideas = ideas.map((row) => (row.id === id ? { ...row, votes: row.votes + 1, voted: true } : row));
        ideas.sort((a, b) => b.votes - a.votes || b.createdAt - a.createdAt);
        track("feedback_voted");
        render();
      })
      .catch(() => {
        ideasError = "Could not vote";
        render();
      });
  } else if (act === "remove" && target.dataset.id) {
    event.stopPropagation();
    const removed = state.people.find((p) => placeId(p) === target.dataset.id);
    const people = state.people.filter((p) => placeId(p) !== target.dataset.id);
    state = { ...state, people, activeId: people[0] ? placeId(people[0]) : null };
    if (roomId && removed?.memberId) {
      void patchRoom(roomId, { removeMemberId: removed.memberId }).catch(() => onRoomExpired());
    }
    render();
  } else if (act === "add" && target.dataset.pid) {
    const [iana, city, province] = target.dataset.pid.split("|");
    const place = findPlace(iana, city ?? "", province);
    if (place) addPlace(place);
  } else if (act === "prev") {
    state = { ...state, date: shiftDate(state.date, -1) };
    void publishMine();
    void refreshBusyFromSources();
    render();
  } else if (act === "next") {
    state = { ...state, date: shiftDate(state.date, 1) };
    void publishMine();
    void refreshBusyFromSources();
    render();
  } else if (act === "cal-menu") {
    showCalMenu = !showCalMenu;
    showGoogleSetup = false;
    render();
  } else if (act === "cal-google") {
    if (!googleClientId()) {
      showGoogleSetup = true;
      render();
    } else {
      void syncGoogle();
    }
  } else if (act === "cal-google-save") {
    const input = document.querySelector<HTMLInputElement>("#google-client-id");
    const value = input?.value.trim() ?? "";
    if (!value.includes(".apps.googleusercontent.com")) {
      flash("Paste the Client ID from Google Cloud (it ends with .apps.googleusercontent.com)");
      return;
    }
    setGoogleClientId(value);
    void syncGoogle();
  } else if (act === "cal-ics") {
    document.querySelector<HTMLInputElement>("#ics-file")?.click();
  } else if (act === "cal-clear") {
    clearCalendarSession();
    setBusy([]);
    showCalMenu = false;
    flash("Busy hours cleared");
    render();
  } else if (act === "busy") {
    flash("That hour is busy on the calendar");
  } else if (act === "dur" && target.dataset.mins) {
    const mins = Number(target.dataset.mins);
    if (mins === 15 || mins === 30 || mins === 45 || mins === 60) {
      state = { ...state, duration: parseDuration(mins) };
      void publishMine();
      render();
    }
  } else if (act === "slot" && target.dataset.hour) {
    const hour = Number(target.dataset.hour);
    state = { ...state, selectedHour: state.selectedHour === hour ? null : hour };
    void publishMine();
    render();
  } else if (act === "copy") {
    const link = roomId ? roomUrl(roomId) : location.href;
    void navigator.clipboard.writeText(link).then(
      () => {
        if (roomId) track("room_link_copied");
        flash(roomId ? "Room link copied" : "Link copied");
      },
      () => flash("Copy the URL from the address bar"),
    );
  }
});

app.addEventListener("change", (event) => {
  const input = event.target as HTMLInputElement;
  if (input.id !== "ics-file" || !input.files?.[0]) return;
  void syncIcs(input.files[0]);
});

app.addEventListener("input", (event) => {
  const input = event.target as HTMLInputElement;
  if (input.id !== "tz-search") return;
  query = input.value;
  highlight = 0;
  render();
});

app.addEventListener("keydown", (event) => {
  if (!showSearch) return;
  const matches = results();
  if (event.key === "Escape") {
    showSearch = false;
    render();
  } else if (event.key === "ArrowDown") {
    event.preventDefault();
    highlight = Math.min(matches.length - 1, highlight + 1);
    render();
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    highlight = Math.max(0, highlight - 1);
    render();
  } else if (event.key === "Enter" && matches[highlight]) {
    event.preventDefault();
    addPlace(matches[highlight]);
  }
});

window.addEventListener("hashchange", () => {
  if (isPrivacyPath()) return;
  state = decodeState(location.hash) ?? freshState();
  render();
});

window.addEventListener("popstate", () => {
  render();
});

setInterval(() => {
  now = new Date();
  const clocks = document.querySelectorAll(".chip-time .clock, .live-utc");
  if (!clocks.length) return;
  const utc = document.querySelector(".live-utc");
  if (utc) utc.innerHTML = `<span class="pulse"></span>${escapeHtml(formatUtcClock(now))}`;
  document.querySelectorAll(".chip").forEach((chip) => {
    const id = (chip as HTMLElement).dataset.id;
    const person = state.people.find((p) => placeId(p) === id);
    if (!person) return;
    const clock = chip.querySelector(".clock");
    const offset = chip.querySelector(".offset");
    if (clock) clock.textContent = formatClock(now, person.iana);
    if (offset) offset.textContent = utcOffsetLabel(now, person.iana);
  });
}, 1000);

if (roomId) void startRoom(roomId);
else render();
